/**
 * Console -> native messages for the phone app's embedded console.
 *
 * Every message is a plain JSON object with a `type`. On iOS it goes to
 * `window.webkit.messageHandlers[<bridge>].postMessage(message)`; on Android to
 * `window[<bridge>].postMessage(JSON.stringify(message))`, because a
 * `@JavascriptInterface` method only takes strings. Outside the app every send
 * is a no-op that reports `false`, so callers never branch on the host.
 *
 * Native -> console calls land on `window.__nimbalystConsoleBridge`; see
 * `nativeBridgeApi.ts`.
 *
 * Tokens never travel in this direction. `requestSession` and `sessionExpired`
 * carry an org id and a request id, never a credential.
 */

import { getEmbedMarker } from './embedMarker';

export const CONSOLE_BRIDGE_PROTOCOL = 1;

export type FlushPendingStatus = 'acknowledged' | 'not-required' | 'timed-out' | 'failed';

export type ConsoleToNativeMessage =
  | { type: 'ready'; protocol: typeof CONSOLE_BRIDGE_PROTOCOL }
  | { type: 'route'; path: string; title: string; canGoBack: boolean }
  | { type: 'editState'; editing: boolean; unsynced: boolean }
  | { type: 'requestSession'; requestId: string; orgId: string }
  | { type: 'sessionExpired'; requestId: string; orgId: string | null }
  | { type: 'orgAuthRequired'; orgId: string; reason: string | null }
  | { type: 'openExternal'; url: string }
  | { type: 'openPersonal'; path: string }
  | { type: 'flushResult'; requestId: string; status: FlushPendingStatus; detail: string | null };

interface WebKitMessageHandler { postMessage(message: unknown): void }
interface AndroidMessageHandler { postMessage(message: string): void }

declare global {
  interface Window {
    webkit?: { messageHandlers?: Record<string, WebKitMessageHandler | undefined> };
  }
}

/** Send one message to the hosting app. Returns false when there is no host to receive it. */
export function postToNative(message: ConsoleToNativeMessage): boolean {
  const marker = getEmbedMarker();
  if (!marker) return false;
  try {
    if (marker.platform === 'ios') {
      const handler = window.webkit?.messageHandlers?.[marker.bridge];
      if (!handler) return false;
      handler.postMessage(message);
      return true;
    }
    const handler = (window as unknown as Record<string, AndroidMessageHandler | undefined>)[marker.bridge];
    if (!handler || typeof handler.postMessage !== 'function') return false;
    handler.postMessage(JSON.stringify(message));
    return true;
  } catch (cause) {
    console.warn('[embedded] Native bridge post failed', message.type, cause);
    return false;
  }
}

export function openExternalInNative(url: string): boolean {
  return postToNative({ type: 'openExternal', url });
}

export function openPersonalInNative(path: string): boolean {
  return postToNative({ type: 'openPersonal', path });
}
